import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import YoutubeButton from "../YoutubeButton/YoutubeButton";

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
    <path d="M23.5 6.2a3.01 3.01 0 0 0-2.12-2.13C19.54 3.5 12 3.5 12 3.5s-7.54 0-9.38.57A3.01 3.01 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3.01 3.01 0 0 0 2.12 2.13C4.46 20.5 12 20.5 12 20.5s7.54 0 9.38-.57a3.01 3.01 0 0 0 2.12-2.13A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z" />
  </svg>
);

const UploadButton = ({ onFileLoad, styles }) => {
  const [mode, setMode] = useState("upload"); // "upload" | "youtube"
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const loadFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
      alert("Please upload an audio or video file (mp3, wav, flac, mp4, mov, etc.)");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => onFileLoad(e.target.result, file.name);
    reader.readAsArrayBuffer(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  };

  const isYT = mode === "youtube";

  const containerClass = [
    styles.uploadContainer,
    isYT ? styles.uploadContainerYoutube : "",
    !isYT && fileName ? styles.uploadContainerLoaded : "",
    !isYT && isDragging ? styles.uploadContainerDragging : "",
  ]
    .filter(Boolean)
    .join(" ");

  const uploadButtonClass = [
    styles.uploadButton,
    fileName ? styles.uploadButtonLoaded : "",
    isDragging ? styles.uploadButtonDragging : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClass}>
      <div className={styles.uploadSlider}>
        {/* Pane 1 — file upload, always static */}
        <div
          className={styles.uploadPane}
          style={{ pointerEvents: isYT ? "none" : "auto" }}
        >
          <input
            type="file"
            accept=".mp3,.wav,.flac,.m4a,.aac,.ogg,.mp4,.mov"
            ref={fileInputRef}
            onChange={(e) => loadFile(e.target.files[0])}
            style={{ display: "none" }}
          />
          <button
            className={uploadButtonClass}
            onClick={() => fileInputRef.current.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
            <div className={styles.uploadTextWrap}>
              <span className={styles.uploadTitle}>
                {fileName ? fileName.replace(/\.[^/.]+$/, "") : "Upload Audio"}
              </span>
              <span className={styles.uploadSub}>
                {fileName ? "Click to replace" : "MP3 · WAV · FLAC · MP4 · MOV"}
              </span>
            </div>
          </button>

          <button
            className={styles.ytIconButton}
            onClick={() => setMode("youtube")}
            title="Load from YouTube"
          >
            <YouTubeIcon />
          </button>
        </div>

        {/* Pane 2 — YouTube overlay, slides in from right */}
        <div
          className={styles.ytPane}
          style={{
            opacity: isYT ? 1 : 0,
            pointerEvents: isYT ? "auto" : "none",
          }}
        >
          <YoutubeButton
            visible={isYT}
            onFileLoad={(buffer, name) => {
              onFileLoad(buffer, name);
              setMode("upload");
            }}
            onCancel={() => setMode("upload")}
          />
        </div>
      </div>
    </div>
  );
};

export default UploadButton;
